// Frontend duplicate-name prevention for the settings-area uploads. The backend
// stores files under a timestamped name and never replaces, so re-uploading the
// same file silently creates a duplicate doc; this guards against that at the UI.

/** Strip the `YYYYMMDD_HHMMSS_` prefix the backend prepends to stored doc names. */
export function storedDocDisplayName(docUrl: string): string {
  const base = docUrl.split('/').pop() ?? docUrl
  return base.replace(/^\d{8}_\d{6}_/, '')
}

/**
 * Normalised key that survives the server's `secure_filename` rewrite, so a raw
 * dropped name compares equal to the mutated name a stored doc carries. Mirror its
 * transform: strip accents to ASCII (macrons — "Māori" → "Maori"), drop the chars it
 * discards (anything outside letters/digits/`_.-`, e.g. parens and commas), then fold
 * case and treat spaces/underscores as one separator ("My File.pdf" → "My_File.pdf").
 */
export function fileNameKey(name: string): string {
  return name
    .normalize('NFKD') // decompose accents: "ā" → "a" + combining macron
    .replace(/[\u0300-\u036f]/g, '') // drop the combining marks left behind
    .toLowerCase()
    .replace(/[^a-z0-9_.\- ]+/g, '') // drop chars secure_filename strips: parens, commas, undecomposable non-ASCII
    .replace(/[\s_]+/g, ' ') // spaces and underscores are the same separator
    .trim()
}

/**
 * Normalised key for website URLs (lower-case, trailing slashes ignored, scheme
 * ignored). The backend's own duplicate check treats http://, https://, and a
 * scheme-less URL as the same site (file_upload.py's check_existing_and_validate_url
 * probes all three), so the client-side pre-check has to match that or it misses a
 * duplicate the backend would still catch - deferring the feedback from an
 * immediate client-side message to the async upload job's own failure.
 */
export function websiteUrlKey(url: string): string {
  return url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '')
}

export interface DedupeResult {
  /** Files safe to stage (no clash with staged, already-uploaded, or each other). */
  accepted: File[]
  /** Display names of dropped files skipped as duplicates. */
  duplicates: string[]
}

/**
 * Split freshly-dropped files into those safe to stage and those that duplicate an
 * already-staged file, an already-uploaded doc, or an earlier file in the same drop.
 */
export function dedupeDroppedFiles(
  dropped: File[],
  stagedNames: string[],
  uploadedNames: string[],
): DedupeResult {
  const seen = new Set<string>([
    ...stagedNames.map(fileNameKey),
    ...uploadedNames.map(fileNameKey),
  ])
  const accepted: File[] = []
  const duplicates: string[] = []
  for (const file of dropped) {
    const key = fileNameKey(file.name)
    if (seen.has(key)) {
      duplicates.push(file.name)
    } else {
      seen.add(key)
      accepted.push(file)
    }
  }
  return { accepted, duplicates }
}

/** Whether a website URL already appears in the staged or already-uploaded lists. */
export function isDuplicateWebsite(
  url: string,
  stagedUrls: string[],
  uploadedUrls: string[],
): boolean {
  const key = websiteUrlKey(url)
  return [...stagedUrls, ...uploadedUrls].some((u) => websiteUrlKey(u) === key)
}
